import { useEffect, useMemo, useState } from 'react';
import './QuoteCopyModal.css';

import { Card, Button, Form, Row, Col } from 'react-bootstrap';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import { FlexGrid, FlexGridColumn, FlexGridCellTemplate } from '@grapecity/wijmo.react.grid';
import * as wjcGrid from '@grapecity/wijmo.grid';
import { CellType } from '@grapecity/wijmo.grid';

import { useMaster } from 'context/MasterContext';
import { useRestApi } from 'context/RestApiContext';

// 集計(グループ)行のセル結合
class CustomMergeManager extends wjcGrid.MergeManager {
  getMergedRange(panel, r, c, clip = true) {
    const rng = new wjcGrid.CellRange(r, c);
    const dataItem = panel?.grid?.rows?.[r]?.dataItem;

    if (panel.cellType === CellType.Cell && dataItem?.Group) {
      if (c >= 1 && c < 4) {
        rng.col = 1;
        rng.col2 = 3;
        return rng;
      }
      if (c > 4 && c < 7) {
        rng.col = 5;
        rng.col2 = 6;
        return rng;
      }
    }

    return rng;
  }
}

function QuoteDetailModal({
  show,
  setShow,
  quote,
  onCopy,
  onCloseParent,
  useLatestPrice,
  setUseLatestPrice,
}) {
  const restApi = useRestApi();
  const master = useMaster();

  const [viewQuoteDetails, setViewQuoteDetails] = useState([]);
  const [checkedItems, setCheckedItems] = useState(new Set());

  const groupKnos = useMemo(() => {
    if (!quote?.kbns) return [];
    return quote.kbns.map((k) => k.QUOTE_KNO).sort((a, b) => a - b);
  }, [quote]);

  const allChecked = groupKnos.length > 0 && groupKnos.every((kno) => checkedItems.has(kno));
  const someChecked = groupKnos.some((kno) => checkedItems.has(kno));

  const planerWithEmpty = useMemo(() => [...master.planer, { id: 0, name: '' }], [master.planer]);

  const initializedGrid = (flex) => {
    flex.mergeManager = new CustomMergeManager();
  };

  useEffect(() => {
    if (!quote?.kbns || !quote?.details) {
      setViewQuoteDetails([]);
      return;
    }

    const rows = [];
    let currentKno = null;

    quote.details.forEach((detail) => {
      if (currentKno !== detail.QUOTE_KNO) {
        currentKno = detail.QUOTE_KNO;

        const kbn = quote.kbns.find((x) => x.QUOTE_KNO === detail.QUOTE_KNO);
        rows.push({
          Group: true,
          QUOTE_KNO: detail.QUOTE_KNO,
          CONTENT: kbn
            ? `${String.fromCharCode(64 + detail.QUOTE_KNO)}. ${kbn.KOUJI_DISPLAY_NAME || kbn.KOUJI_SYU}`
            : '',
          KINGAKU: kbn ? kbn.TOTAL : 0,
        });
      }

      rows.push(detail);
    });

    setViewQuoteDetails(rows);
    setCheckedItems(new Set());
  }, [quote]);

  const handleCheckChange = (quoteKno, checked) => {
    setCheckedItems((prev) => {
      const next = new Set(prev);
      if (checked) next.add(quoteKno);
      else next.delete(quoteKno);
      return next;
    });
  };

  const handleToggleAll = (checked) => {
    setCheckedItems(checked ? new Set(groupKnos) : new Set());
  };

  // 個別コピー
  const handleAdd = () => {
    if (checkedItems.size === 0) {
      alert('工事種別を選択してください');
      return;
    }

    const selectedKbns = quote.kbns.filter((k) => checkedItems.has(k.QUOTE_KNO));
    const selectedDetails = quote.details.filter((d) => checkedItems.has(d.QUOTE_KNO));

    const doCopy = (details) => {
      onCopy?.(quote.QUOTE_NO, { kbns: selectedKbns, details });
      setShow(false);
      onCloseParent?.();
    };

    // 最新単価を使わないならそのまま
    if (!useLatestPrice) {
      doCopy(selectedDetails);
      return;
    }

    // BASE_NO のリストを取得
    const baseNos = [
      ...new Set(
        selectedDetails.filter((d) => d.BASE_NO && d.BASE_NO > 0).map((d) => d.BASE_NO),
      ),
    ];

    // BASE_NO が無いならそのまま
    if (baseNos.length === 0) {
      doCopy(selectedDetails);
      return;
    }

    // 最新単価を取得して明細へ反映
    restApi.post('/api/quote/latest-prices', { BASE_NOS: baseNos }, (priceMap) => {
      const updatedDetails = selectedDetails.map((detail) => {
        const latest = detail.BASE_NO ? priceMap?.[String(detail.BASE_NO)] : null;
        if (!latest) return detail;

        const su = Number(detail.SU ?? 0);
        const tankA = Number(latest.TANKA) || Number(detail.TANKA) || 0;
        const genkaTankA = Number(latest.GENKA_TANKA) || Number(detail.GENKA_TANKA) || 0;

        if (detail.KOUJI_TYPE === 3 && Number(detail.KOUJIHI_PER ?? 0) <= 100) {
          return {
            ...detail,
            TANKA: tankA,
            GENKA_RATE: Number(latest.GENKA_RATE ?? detail.GENKA_RATE ?? 0),
            GENKA_TANKA: genkaTankA,
          };
        }

        const kingaku = su * tankA;
        const genkaKingaku = su * genkaTankA;

        return {
          ...detail,
          TANKA: tankA,
          GENKA_RATE: Number(latest.GENKA_RATE ?? detail.GENKA_RATE ?? 0),
          GENKA_TANKA: genkaTankA,
          KINGAKU: kingaku,
          GENKA_KINGAKU: genkaKingaku,
        };
      });

      doCopy(updatedDetails);
    });
  };

  // グループ行だけ背景色を変える
  const formatItem = (grid, e) => {
    if (e.panel.cellType !== CellType.Cell) return;

    const dataItem = grid.rows[e.row].dataItem;
    if (dataItem?.Group) {
      e.cell.style.background = 'rgb(255, 160, 122)';
      e.cell.style.color = 'rgb(255, 255, 255)';
      grid.rows[e.row].height = 35;
      return;
    }

    e.cell.style.background = '';
    e.cell.style.color = '';
  };

  return (
    <Dialog
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          width: '1600px',
          maxWidth: '95vw',
        },
      }}
      open={show}
      disableEnforceFocus
    >
      <DialogTitle className="p-3">見積詳細 - {quote?.KOUJI_MEI}</DialogTitle>

      <Card className="m-0">
        <Card.Body>
          <Form>
            <Row>
              <Col md="4" className="pr-1">
                <Form.Group>
                  <label className="item-header-label">工事名</label>
                  <Form.Control value={quote?.KOUJI_MEI ?? ''} disabled />
                </Form.Group>
              </Col>

              <Col md="4" className="pr-1">
                <Form.Group>
                  <label className="item-header-label">住所</label>
                  <Form.Control value={quote?.ADDRESS ?? ''} disabled />
                </Form.Group>
              </Col>

              <Col md="2" className="pr-1">
                <Form.Group>
                  <label className="item-header-label">顧客名</label>
                  <Form.Control value={quote?.CLIENT_NAME ?? ''} disabled />
                </Form.Group>
              </Col>

              <Col md="2" className="pr-1">
                <Form.Group>
                  <label className="item-header-label">営業担当</label>
                  <Form.Control
                    value={planerWithEmpty.find((p) => p.id === quote?.SALES_TAN_CD)?.name || ''}
                    disabled
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md="2" className="pr-1">
                <Form.Group>
                  <label className="item-header-label">金額</label>
                  <Form.Control
                    value={quote?.MITSU != null ? `¥${Number(quote.MITSU).toLocaleString()}` : ''}
                    disabled
                  />
                </Form.Group>
              </Col>

              <Col md="2" className="pr-1">
                <Form.Group>
                  <label className="item-header-label">見積日</label>
                  <Form.Control value={quote?.MITSU_DATE ?? ''} disabled />
                </Form.Group>
              </Col>
            </Row>
          </Form>

          <DialogContent>
            <div style={{ flex: 1, overflow: 'auto', height: 'calc(90vh - 320px)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <input
                  type="checkbox"
                  checked={allChecked}
                  ref={(el) => {
                    if (el) el.indeterminate = !allChecked && someChecked;
                  }}
                  onChange={(e) => handleToggleAll(e.target.checked)}
                />
                <span>全選択</span>
              </div>

              <FlexGrid
                itemsSource={viewQuoteDetails}
                selectionMode="None"
                headersVisibility="Column"
                allowSorting={false}
                initialized={initializedGrid}
                formatItem={formatItem}
                isReadOnly
              >
                <FlexGridColumn binding="" header="" width={40}>
                  <FlexGridCellTemplate
                    cellType="Cell"
                    template={(cell) =>
                      cell.item?.Group ? (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checkedItems.has(cell.item.QUOTE_KNO)}
                            onChange={(e) =>
                              handleCheckChange(cell.item.QUOTE_KNO, e.target.checked)
                            }
                            style={{ width: 18, height: 18, cursor: 'pointer' }}
                          />
                        </div>
                      ) : null
                    }
                  />
                </FlexGridColumn>

                <FlexGridColumn binding="CONTENT" header="名称" width={250} />
                <FlexGridColumn binding="SIYOU" header="仕様" width={200} />
                <FlexGridColumn binding="SU" header="数量" format="n2" width={80} dataType="Number" />
                <FlexGridColumn binding="TANI" header="単位" width={60} />
                <FlexGridColumn binding="TANKA" header="単価" format="c0" width={100} dataType="Number" />
                <FlexGridColumn binding="KINGAKU" header="金額" format="c0" width={120} dataType="Number" />
                <FlexGridColumn binding="BIKO" header="備考" width={300} />
                <FlexGridColumn binding="BIKO_SYANAI" header="社内備考" width="1*" />
              </FlexGrid>
            </div>
          </DialogContent>
        </Card.Body>
      </Card>

      <DialogActions className="p-3" style={{ justifyContent: 'space-between' }}>
        <Button variant="secondary" className="footer-button" onClick={() => setShow(false)}>
          キャンセル
        </Button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label>
            <input
              className="mr-1"
              style={{ transform: 'scale(1.5)' }}
              type="checkbox"
              checked={useLatestPrice}
              onChange={(e) => setUseLatestPrice(e.target.checked)}
            />
            最新の単価でコピー
          </label>

          <Button variant="primary" onClick={handleAdd} className="btn-fill footer-button">
            追加
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
}

export default QuoteDetailModal;